/**
 * useCheckoutFlow - Enhanced checkout flow with auth handling
 *
 * Handles the complete checkout process:
 * 1. User selects plan → planId chosen
 * 2. Auth check → login/signup if needed
 * 3. Backend creates checkout session
 * 4. Redirect to payment gateway
 */

import { useCallback, useState, useEffect } from 'react';
import { useUseCelo, useCheckout } from '../providers/UseCeloProvider';
// Auth now handled via simple userId from context
import { CheckoutOptions } from '../types/payment';

export interface CheckoutFlowOptions {
  // URLs for success/cancel
  successUrl?: string;
  cancelUrl?: string;

  // Auth behavior
  onAuthRequired?: (planId: string) => void | Promise<void>;
  authRedirectUrl?: string;

  // External auth override (bypasses SDK auth detection)
  externalAuth?: {
    isAuthenticated: boolean;
    user: {
      id: string;
      email?: string;
      name?: string;
    } | null;
  };

  // Checkout options
  allowPromotionCodes?: boolean;
  collectShippingAddress?: boolean;
  mode?: 'subscription' | 'payment';

  // Callbacks
  onCheckoutStarted?: (planId: string) => void;
  onCheckoutError?: (error: string, planId: string) => void;

  // Testing/Development
  useDefaultPages?: boolean; // Use our default success/cancel pages
  testMode?: boolean; // Use mock checkout for testing
  hardcodedUserId?: string; // For testing: bypass auth and use this user ID
}

export function useCheckoutFlow(options: CheckoutFlowOptions = {}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get userId, appId and apiHost from context - simplified approach
  const { userId: userIdFromContext, appId, apiHost } = useUseCelo();

  // Auth is now handled via userId prop or externalAuth option

  // Get checkout functionality
  const { createCheckout, getCheckoutUrl, isReady, gateway } = useCheckout();

  const {
    successUrl = typeof window !== 'undefined' ? `${window.location.origin}/checkout/success` : '/checkout/success',
    cancelUrl = typeof window !== 'undefined' ? window.location.href : '/pricing',
    onAuthRequired,
    authRedirectUrl = '/login',
    externalAuth,
    allowPromotionCodes = true,
    mode = 'subscription',
    onCheckoutStarted,
    onCheckoutError,
    useDefaultPages = false,
    testMode = false,
    hardcodedUserId
  } = options;

  /**
   * Main checkout function - connects to PricingTable CTA
   */
  const startCheckout = useCallback(async (planId: string, customOptions?: Partial<CheckoutOptions>) => {
    try {
      setIsProcessing(true);
      setError(null);

      console.log('🛒 Starting checkout for plan:', planId);

      // Step 1: Check if payment is configured
      console.log('✅ Step 1: Checking payment configuration...');
      if (!isReady) {
        console.log('❌ Payment system is not configured');
        throw new Error('Payment system is not configured');
      }
      console.log('✅ Payment system is configured');

      // Step 2: Auth check (use hardcoded userId for testing, external auth, or SDK auth)
      console.log('✅ Step 2: Checking authentication...');

      let finalIsAuthenticated: boolean;
      let finalUser: { id: string; email?: string; name?: string } | null;

      // Simplified auth resolution priority:
      // 1. hardcodedUserId (testing)
      // 2. externalAuth (like Clerk passed from PricingTable)
      // 3. userId from context (new simplified approach)

      if (hardcodedUserId) {
        // Testing mode: use hardcoded user ID
        finalIsAuthenticated = true;
        finalUser = { id: hardcodedUserId, email: `test-${hardcodedUserId}@example.com` };
        console.log('🧪 Using hardcoded userId for testing:', hardcodedUserId);
      } else if (externalAuth) {
        // Use external auth (like Clerk)
        finalIsAuthenticated = externalAuth.isAuthenticated;
        finalUser = externalAuth.user;
        console.log('🔐 Using external auth:', externalAuth.user?.id);
      } else if (userIdFromContext) {
        // Use userId from context - new simplified approach
        const resolvedUserId = typeof userIdFromContext === 'function'
          ? await userIdFromContext()
          : userIdFromContext;

        if (resolvedUserId) {
          finalIsAuthenticated = true;
          finalUser = { id: resolvedUserId };
          console.log('✅ Using userId from context:', resolvedUserId);
        } else {
          finalIsAuthenticated = false;
          finalUser = null;
        }
      } else {
        // No auth available
        finalIsAuthenticated = false;
        finalUser = null;
      }

      console.log('🔍 Auth state determination:', {
        hardcodedUserId: hardcodedUserId || null,
        hasExternalAuth: !!externalAuth,
        externalAuth: externalAuth ? {
          isAuthenticated: externalAuth.isAuthenticated,
          userId: externalAuth.user?.id
        } : null,
        final: {
          isAuthenticated: finalIsAuthenticated,
          userId: finalUser?.id
        }
      });

      if (!finalIsAuthenticated || !finalUser?.id) {
        console.log('🔐 Authentication required for checkout');
        console.log('Auth redirect URL:', authRedirectUrl);

        // Store pending checkout
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('usecelo_pending_checkout', JSON.stringify({
            planId,
            timestamp: Date.now(),
            options: customOptions
          }));
        }

        // Handle auth requirement
        if (onAuthRequired) {
          console.log('Using custom onAuthRequired handler');
          await onAuthRequired(planId);
        } else if (authRedirectUrl) {
          // Default: redirect to auth page with redirect_url for Clerk
          console.log('Redirecting to auth page:', authRedirectUrl);
          if (typeof window !== 'undefined') {
            const currentUrl = window.location.href;
            const redirectUrl = `${authRedirectUrl}?redirect_url=${encodeURIComponent(currentUrl)}&plan=${planId}`;
            console.log('Full redirect URL:', redirectUrl);
            window.location.href = redirectUrl;
          }
        } else {
          // If no auth redirect URL provided, throw error
          console.error('No authentication method available. Please sign in to continue.');
          throw new Error('Authentication required. Please provide authRedirectUrl or onAuthRequired handler.');
        }
        return;
      }
      console.log('✅ User is authenticated:', finalUser.id);

      // Step 3: Notify checkout started
      console.log('✅ Step 3: Notifying checkout started...');
      onCheckoutStarted?.(planId);

      // Step 4: Build checkout options
      console.log('✅ Step 4: Building checkout options...');

      // Prepare client redirect URLs
      const origin = process.env.NEXT_PUBLIC_APP_URL ||
                     (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');

      // Convert relative URLs to absolute URLs for client redirects
      const clientSuccessUrl = successUrl.startsWith('http') ? successUrl : `${origin}${successUrl}`;
      const clientCancelUrl = cancelUrl.startsWith('http') ? cancelUrl : `${origin}${cancelUrl}`;

      // Build UseCelo hosted checkout URLs
      // These URLs will handle the checkout flow and then redirect back to the client
      const hostedSuccessUrl = `${apiHost}/api/public/checkout/success/${appId}`;
      const hostedCancelUrl = `${apiHost}/api/public/checkout/cancel/${appId}`;

      console.log('🔗 Using hosted checkout URLs:', {
        success: hostedSuccessUrl,
        cancel: hostedCancelUrl,
        clientRedirects: {
          success: clientSuccessUrl,
          cancel: clientCancelUrl
        }
      });

      const checkoutOptions: CheckoutOptions = {
        planId,
        // Use UseCelo hosted URLs with client redirect URLs as query params
        successUrl: useDefaultPages
          ? `${hostedSuccessUrl}?session_id={CHECKOUT_SESSION_ID}&client_success=${encodeURIComponent(`${origin}/checkout/success`)}`
          : `${hostedSuccessUrl}?session_id={CHECKOUT_SESSION_ID}&client_success=${encodeURIComponent(clientSuccessUrl)}`,
        cancelUrl: useDefaultPages
          ? `${hostedCancelUrl}?plan=${planId}&client_cancel=${encodeURIComponent(`${origin}/pricing`)}`
          : `${hostedCancelUrl}?plan=${planId}&client_cancel=${encodeURIComponent(clientCancelUrl)}`,
        userId: finalUser.id,
        email: finalUser.email,
        metadata: {
          planId,
          userId: finalUser.id,
          source: 'pricing_table',
          timestamp: new Date().toISOString(),
          mode,
          allowPromotionCodes: allowPromotionCodes.toString()
        },
        ...customOptions
      };

      console.log('📡 Creating checkout session with options:', checkoutOptions);

      // Step 5: Create checkout session
      console.log('✅ Step 5: Creating checkout session...');
      if (testMode) {
        // Mock checkout for testing
        console.log('🧪 Test mode: Simulating checkout');
        const mockUrl = `/mock-checkout?plan=${planId}&user=${finalUser.id}`;

        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay

        if (typeof window !== 'undefined') {
          window.location.href = mockUrl;
        }
      } else {
        // Real checkout
        console.log('🔄 Creating real checkout session...');
        const session = await createCheckout(checkoutOptions);
        console.log('✅ Checkout session created:', session);

        // Step 6: Redirect to payment gateway
        console.log('🚀 Redirecting to Stripe:', session.url);
        if (typeof window !== 'undefined') {
          console.log('🌐 Performing redirect...');
          window.location.href = session.url;
        } else {
          console.log('❌ Window not available for redirect');
        }
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Checkout failed';
      console.error('❌ Checkout error:', errorMessage);
      setError(errorMessage);
      onCheckoutError?.(errorMessage, planId);
    } finally {
      setIsProcessing(false);
    }
  }, [
    isReady,
    createCheckout,
    successUrl,
    cancelUrl,
    onAuthRequired,
    authRedirectUrl,
    allowPromotionCodes,
    mode,
    onCheckoutStarted,
    onCheckoutError,
    useDefaultPages,
    testMode
  ]);

  /**
   * Resume checkout after authentication
   */
  const resumePendingCheckout = useCallback(async () => {
    // No longer using SDK auth - this would be handled by external auth systems
    return;

    if (typeof window === 'undefined') return;

    const pendingData = sessionStorage.getItem('usecelo_pending_checkout');
    if (!pendingData) return;

    try {
      const { planId, timestamp, options } = JSON.parse(pendingData as string);

      // Check if not too old (10 minutes)
      if (Date.now() - timestamp > 10 * 60 * 1000) {
        sessionStorage.removeItem('usecelo_pending_checkout');
        return;
      }

      console.log('🔄 Resuming pending checkout for plan:', planId);
      sessionStorage.removeItem('usecelo_pending_checkout');

      await startCheckout(planId, options);
    } catch (err) {
      console.error('Failed to resume checkout:', err);
      sessionStorage.removeItem('usecelo_pending_checkout');
    }
  }, [startCheckout]);

  // Auto-resume on mount - no longer needed with simplified auth
  // External auth systems handle their own redirect logic

  return {
    // Main checkout function
    startCheckout,

    // Resume after auth
    resumePendingCheckout,

    // State
    isProcessing,
    error,
    isReady,
    gateway
  };
}

/**
 * Helper hook for checkout success pages
 */
export function useCheckoutSuccess() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const { refreshFeatures } = useUseCelo();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const session = params.get('session_id');
    const plan = params.get('plan');

    if (session) {
      setSessionId(session);
      setPlanId(plan);

      // Refresh features to unlock new plan
      refreshFeatures();

      // Clear any pending checkout
      sessionStorage.removeItem('usecelo_pending_checkout');
    }
  }, [refreshFeatures]);

  return {
    sessionId,
    planId,
    isSuccess: !!sessionId
  };
}

/**
 * Helper hook for checkout cancel pages
 */
export function useCheckoutCancel() {
  const [planId, setPlanId] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');

    if (plan) {
      setPlanId(plan);
    }
  }, []);

  return {
    planId,
    isCancelled: true
  };
}